/*
  ff-detangle-challenge.js — <ff-detangle-challenge> for sections/ff-detangle-challenge.liquid

  "The 7-Second Challenge" (prototype). A canvas strand of hair with knots;
  swiping DOWN over a knot brushes it out (upward strokes do nothing, like a
  real comb). Spray (7SECONDS) makes knots take ~2.4x damage for 2.5s. The
  clock starts on the first stroke or spray; clearing every knot stops it,
  7.00s ends the round. Finish time → reward tier:
      perfect  < tier1 seconds
      great    < tier2 seconds
      finished everyone else (including time-outs and "skip the game")
  Best of `attempts` tries counts.

  Claim: Klaviyo client API (public key) — subscribe to the list when set,
  plus a "Completed 7-Second Challenge" event, with profile properties
  Challenge Tier / Challenge Reward / Challenge Time so the flow can send
  the matching code. Then optional tap-to-text (Postscript keyword) and a
  share card (1080x1920 PNG of your time via the Web Share API).

  GA4 (window.ffTrack): challenge_view, challenge_start, challenge_finish,
  challenge_retry, challenge_skip, generate_lead (lead_source=insert_challenge),
  challenge_sms_click, challenge_share, challenge_shop_click. ?ff_silent=1 for tests.
*/
(function () {
  if (customElements.get('ff-detangle-challenge')) return;

  var STORE_KEY = 'ff_detangle_challenge';
  var KNOT_HP = 220;
  var DRY_RATE = 1;
  var WET_RATE = 2.4;
  var WET_MS = 2500;
  var REACH = 56; // px around a knot centre that a stroke affects
  var ACCENT = '#62B5E5';
  var INK = '#121619';
  // dark brunette with a few warm highlights; wet hair reads darker and glossier
  var HAIR = ['rgba(26,21,17,0.78)', 'rgba(40,31,24,0.72)', 'rgba(18,20,22,0.82)', 'rgba(92,68,48,0.55)', 'rgba(128,98,70,0.35)'];
  var HAIR_WET = ['rgba(14,14,15,0.9)', 'rgba(26,22,19,0.88)', 'rgba(10,11,12,0.92)', 'rgba(70,56,44,0.7)', 'rgba(150,170,185,0.45)'];
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var DOMAIN_FIXES = {
    'gmial.com': 'gmail.com', 'gmai.com': 'gmail.com', 'gmal.com': 'gmail.com', 'gamil.com': 'gmail.com', 'gnail.com': 'gmail.com',
    'gmail.co': 'gmail.com', 'gmail.con': 'gmail.com', 'hotmial.com': 'hotmail.com', 'hotmail.co': 'hotmail.com',
    'yahooo.com': 'yahoo.com', 'yahoo.co': 'yahoo.com', 'icloud.co': 'icloud.com', 'icloud.con': 'icloud.com', 'outlook.co': 'outlook.com'
  };
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function track(name, params) {
    if (typeof window.ffTrack === 'function') window.ffTrack(name, params);
  }
  function readStore() {
    try { return JSON.parse(window.localStorage.getItem(STORE_KEY) || 'null'); } catch (e) { return null; }
  }
  function writeStore(v) {
    try { window.localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch (e) { /* private mode */ }
  }
  function rand(a, b) { return a + Math.random() * (b - a); }
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
  function distToSeg(px, py, a, b) {
    var vx = b.x - a.x, vy = b.y - a.y;
    var len = vx * vx + vy * vy;
    var t = len ? clamp(((px - a.x) * vx + (py - a.y) * vy) / len, 0, 1) : 0;
    var dx = px - (a.x + t * vx), dy = py - (a.y + t * vy);
    return Math.sqrt(dx * dx + dy * dy);
  }
  function secs(ms) { return (ms / 1000).toFixed(2); }

  class FfDetangleChallenge extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      var d = this.dataset;
      this.cfg = {
        duration: (parseFloat(d.duration) || 7) * 1000,
        knots: clamp(parseInt(d.knots, 10) || 6, 3, 9),
        attempts: clamp(parseInt(d.attempts, 10) || 3, 1, 5),
        tiers: [
          { key: 'perfect', ms: (parseFloat(d.tier1) || 3.5) * 1000, label: d.tier1Label || '20% off' },
          { key: 'great', ms: (parseFloat(d.tier2) || 5) * 1000, label: d.tier2Label || '15% off' },
          { key: 'finished', ms: Infinity, label: d.baseLabel || '10% off' }
        ]
      };
      var params = new URLSearchParams(window.location.search);
      this.card = (params.get('c') || params.get('utm_content') || 'unknown').slice(0, 60);

      var q = (sel) => this.querySelector(sel);
      this.stage = q('[data-stage]');
      this.canvas = q('[data-canvas]');
      this.ctx = this.canvas.getContext('2d');
      this.fx = q('[data-confetti]');
      this.fxCtx = this.fx.getContext('2d');
      this.hud = q('[data-hud]');
      this.timerEl = q('[data-timer]');
      this.countEl = q('[data-count]');
      this.barEl = q('[data-bar]');
      this.hint = q('[data-hint]');
      this.sprayBtn = q('[data-spray]');
      this.screens = {};
      this.querySelectorAll('[data-screen]').forEach((el) => (this.screens[el.dataset.screen] = el));

      this.store = Object.assign({ attempts: 0, best: null, claimed: false, email: null }, readStore() || {});
      if (this.hasAttribute('data-design-mode')) this.store = { attempts: 0, best: null, claimed: false, email: null };

      this.particles = [];
      this.confetti = [];
      this.wetUntil = 0;
      this.pointer = null;
      this.down = false;
      this.state = 'intro';

      this.syncHeader();
      this.layout(true);
      new ResizeObserver(() => this.layout(false)).observe(this.stage);

      this.bindGame();
      this.bindFlow();

      this.showPanel('intro', false);
      track('challenge_view', { insert_card: this.card });

      // Coming back: show where they left off (hair already brushed out if they finished).
      if (this.store.best && this.store.best.done) this.knots.forEach((k) => { k.alive = false; k.hp = 0; });
      if (this.store.claimed && this.store.best) {
        this.showDone(false);
      } else if (this.store.best && this.store.best.tier !== 'skipped') {
        this.showResult(this.store.best, false);
      }

      this._raf = requestAnimationFrame((t) => this.frame(t));
    }

    disconnectedCallback() {
      cancelAnimationFrame(this._raf);
    }

    /* ================= layout ================= */

    syncHeader() {
      var hdr = document.getElementById('site-control');
      if (hdr) this.style.setProperty('--ff-dc-hdr', Math.round(hdr.getBoundingClientRect().height) + 'px');
    }

    layout(first) {
      var r = this.stage.getBoundingClientRect();
      if (r.width < 10) return;
      this.W = r.width;
      this.H = r.height;
      this.dpr = Math.min(window.devicePixelRatio || 1, 2);
      [this.canvas, this.fx].forEach((c) => {
        c.width = Math.round(this.W * this.dpr);
        c.height = Math.round(this.H * this.dpr);
      });
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      this.fxCtx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

      // knots live in a central band so wide (desktop) stages stay playable
      this.band = Math.min(this.W * 0.9, 620);
      this.bandX = (this.W - this.band) / 2;

      var count = Math.round(clamp(this.band / 2.6, 110, 240));
      if (first || !this.strands || this.strands.length !== count) {
        this.strands = [];
        for (var i = 0; i < count; i++) {
          var roll = Math.random();
          this.strands.push({
            u: (i + Math.random()) / count, // position across the band, 0..1
            ph: rand(0, Math.PI * 2),
            amp: rand(3, 9),
            freq: rand(0.005, 0.009),
            w: rand(0.7, 1.6),
            c: roll < 0.08 ? 4 : roll < 0.2 ? 3 : Math.floor(Math.random() * 3),
            end: rand(0.9, 1.04) // hair ends at slightly different lengths
          });
        }
      }
      if (first || !this.knots) this.placeKnots();
    }

    placeKnots() {
      var n = this.cfg.knots;
      var cols = Math.min(3, Math.ceil(n / 2));
      var rows = Math.ceil(n / cols);
      var ys = rows === 1 ? [0.52] : rows === 2 ? [0.38, 0.66] : [0.33, 0.52, 0.71];
      this.knots = [];
      for (var i = 0; i < n; i++) {
        var c = i % cols, row = Math.floor(i / cols);
        var loops = [];
        for (var l = 0; l < 18; l++) {
          loops.push({ ox: rand(-11, 11), oy: rand(-12, 12), rx: rand(5, 19), ry: rand(4, 12), rot: rand(0, Math.PI), spin: rand(-1, 1) });
        }
        this.knots.push({
          u: (c + 0.5) / cols + rand(-0.06, 0.06),
          v: ys[row] + rand(-0.035, 0.035),
          hp: KNOT_HP,
          alive: true,
          shake: 0,
          loops: loops
        });
      }
      this.cleared = 0;
      this.updateCount();
    }

    kx(k) { return this.bandX + k.u * this.band; }
    ky(k) { return k.v * this.H; }

    /* ================= game input ================= */

    bindGame() {
      var c = this.canvas;
      var pt = (e) => ({ x: e.clientX - this.rect.left, y: e.clientY - this.rect.top });
      c.addEventListener('pointerdown', (e) => {
        if (this.state !== 'ready' && this.state !== 'play') return;
        e.preventDefault();
        try { c.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
        this.rect = c.getBoundingClientRect();
        this.down = true;
        this.last = this.pointer = pt(e);
      });
      c.addEventListener('pointermove', (e) => {
        if (this.state !== 'ready' && this.state !== 'play') return;
        this.rect = this.rect || c.getBoundingClientRect();
        this.pointer = pt(e);
        if (!this.down) return;
        var evs = e.getCoalescedEvents ? e.getCoalescedEvents() : [e];
        if (!evs.length) evs = [e];
        evs.forEach((ev) => {
          var p = pt(ev);
          this.brush(this.last, p);
          this.last = p;
        });
      });
      var up = () => { this.down = false; };
      c.addEventListener('pointerup', up);
      c.addEventListener('pointercancel', up);
      c.addEventListener('pointerleave', () => { if (!this.down) this.pointer = null; });

      this.sprayBtn.addEventListener('click', () => this.spray());
    }

    brush(a, b) {
      var dy = b.y - a.y;
      if (dy <= 0.5) return; // a comb only works going down
      if (this.state === 'ready') this.begin();
      if (this.state !== 'play') return;
      var wet = performance.now() < this.wetUntil;
      var rate = wet ? WET_RATE : DRY_RATE;
      for (var i = 0; i < this.knots.length; i++) {
        var k = this.knots[i];
        if (!k.alive) continue;
        var d = distToSeg(this.kx(k), this.ky(k), a, b);
        if (d >= REACH) continue;
        k.hp -= dy * rate * (1 - 0.5 * d / REACH);
        k.shake = 1;
        if (Math.random() < 0.25) this.fiber(this.kx(k), this.ky(k));
        if (k.hp <= 0) this.clearKnot(k);
      }
    }

    spray() {
      if (this.state === 'ready') this.begin();
      if (this.state !== 'play') return;
      this.wetUntil = performance.now() + WET_MS;
      this.sprayBtn.classList.add('is-wet');
      clearTimeout(this._wetT);
      this._wetT = setTimeout(() => this.sprayBtn.classList.remove('is-wet'), WET_MS);
      // mist drifts in from the spray button
      var br = this.sprayBtn.getBoundingClientRect(), sr = this.stage.getBoundingClientRect();
      var ox = br.left - sr.left + 20, oy = br.top - sr.top + 10;
      var n = reducedMotion ? 20 : 90;
      for (var i = 0; i < n; i++) {
        var ang = rand(-Math.PI * 0.95, -Math.PI * 0.55);
        var sp = rand(4, 13);
        this.particles.push({ t: 'mist', x: ox, y: oy, vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp, r: rand(2, 7), life: 0, max: rand(50, 90) });
      }
      try { navigator.vibrate && navigator.vibrate(8); } catch (e) { /* ignore */ }
    }

    clearKnot(k) {
      k.alive = false;
      k.hp = 0;
      this.cleared++;
      this.updateCount();
      var x = this.kx(k), y = this.ky(k);
      for (var i = 0; i < 16; i++) this.fiber(x, y, true);
      this.particles.push({ t: 'check', x: x, y: y, life: 0, max: 40 });
      try { navigator.vibrate && navigator.vibrate(14); } catch (e) { /* ignore */ }
      if (this.cleared >= this.knots.length) this.finish(true);
    }

    fiber(x, y, burst) {
      var ang = rand(0, Math.PI * 2), sp = burst ? rand(2, 6) : rand(0.5, 2);
      this.particles.push({ t: 'fiber', x: x + rand(-10, 10), y: y + rand(-10, 10), vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp + 1, a: rand(0, Math.PI), len: rand(6, 14), life: 0, max: rand(26, 44) });
    }

    updateCount() {
      if (this.countEl) this.countEl.textContent = this.cleared + '/' + this.cfg.knots + ' knots';
    }

    /* ================= round lifecycle ================= */

    ready(fromRetry) {
      this.placeKnots();
      this.state = 'ready';
      this.dataset.state = 'ready';
      this.wetUntil = 0;
      this.sprayBtn.classList.remove('is-wet');
      this.hidePanels();
      this.hud.hidden = false;
      this.hint.hidden = false;
      this.sprayBtn.hidden = false;
      this.renderTimer(0);
      // bring the whole stage on screen before the clock can start
      var top = this.stage.getBoundingClientRect().top;
      var hdr = parseFloat(getComputedStyle(this).getPropertyValue('--ff-dc-hdr')) || 0;
      if (Math.abs(top - hdr - 10) > 4) window.scrollTo({ top: window.scrollY + top - hdr - 10, behavior: reducedMotion ? 'auto' : 'smooth' });
      if (fromRetry) track('challenge_retry', { insert_card: this.card, attempt: this.store.attempts + 1 });
    }

    begin() {
      this.state = 'play';
      this.dataset.state = 'play';
      this.t0 = performance.now();
      this.hint.hidden = true;
      track('challenge_start', { insert_card: this.card, attempt: this.store.attempts + 1 });
    }

    finish(done) {
      if (this.state !== 'play') return;
      this.state = 'result';
      this.dataset.state = 'result';
      this.down = false;
      this.pointer = null;
      var ms = done ? Math.min(performance.now() - this.t0, this.cfg.duration) : this.cfg.duration;
      this.renderTimer(ms);
      var tier = done ? this.cfg.tiers.find((t) => ms < t.ms) : this.cfg.tiers[2];
      this.store.attempts++;
      var result = { ms: Math.round(ms), done: done, tier: tier.key, label: tier.label, cleared: this.cleared, attempt: this.store.attempts };
      if (this.better(result, this.store.best)) this.store.best = result;
      writeStore(this.store);
      track('challenge_finish', { insert_card: this.card, time_ms: result.ms, completed: done, tier: tier.key, knots_cleared: this.cleared, attempt: result.attempt });
      if (tier.key !== 'finished') this.burst(tier.key === 'perfect' ? 180 : 110);
      setTimeout(() => {
        this.hud.hidden = true;
        this.sprayBtn.hidden = true;
        this.showResult(result, true);
      }, done ? 550 : 250);
    }

    rank(r) { return r ? ['perfect', 'great', 'finished', 'skipped'].indexOf(r.tier) : 99; }

    better(a, b) {
      if (!b) return true;
      if (this.rank(a) !== this.rank(b)) return this.rank(a) < this.rank(b);
      if (a.done !== b.done) return a.done;
      if (!a.done) return a.cleared > b.cleared;
      return a.ms < b.ms;
    }

    /* ================= panels ================= */

    bindFlow() {
      var q = (sel) => this.querySelector(sel);
      q('[data-start]').addEventListener('click', () => this.ready(false));
      q('[data-retry]').addEventListener('click', () => this.ready(true));
      q('[data-claim]').addEventListener('click', () => this.showClaim());
      q('[data-skip-game]').addEventListener('click', () => {
        track('challenge_skip', { insert_card: this.card });
        if (!this.store.best) this.store.best = { ms: null, done: false, tier: 'skipped', label: this.cfg.tiers[2].label, cleared: 0, attempt: 0 };
        writeStore(this.store);
        this.showClaim();
      });

      this.form = this.screens.claim;
      this.input = this.form.querySelector('input[name="email"]');
      this.msg = this.form.querySelector('.ff-dc__msg');
      this.submitBtn = this.form.querySelector('button[type="submit"]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.input.addEventListener('input', () => { this.input.removeAttribute('aria-invalid'); this.msg.textContent = ''; });

      var sms = q('[data-sms-link]');
      this.hasSms = !!(sms && this.dataset.smsNumber && this.dataset.smsKeyword);
      if (this.hasSms) {
        var number = this.dataset.smsNumber.replace(/[^\d+]/g, '');
        if (number.charAt(0) !== '+') number = (number.length === 10 ? '+1' : '+') + number;
        var ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        sms.setAttribute('href', 'sms:' + number + (ios ? '&body=' : '?body=') + encodeURIComponent(this.dataset.smsKeyword));
        sms.addEventListener('click', () => {
          track('challenge_sms_click', { insert_card: this.card });
          setTimeout(() => this.showDone(true), 600);
        });
        q('[data-sms-skip]').addEventListener('click', () => this.showDone(true));
      }

      q('[data-share]').addEventListener('click', () => this.share());
      var shop = q('[data-shop]');
      if (shop) shop.addEventListener('click', () => track('challenge_shop_click', { insert_card: this.card }));
    }

    showPanel(name, focus) {
      Object.keys(this.screens).forEach((k) => (this.screens[k].hidden = k !== name));
      this.dataset.panel = name;
      this.setAttribute('data-panel-open', '');
      if (focus !== false && this.screens[name]) this.screens[name].focus({ preventScroll: true });
    }

    hidePanels() {
      Object.keys(this.screens).forEach((k) => (this.screens[k].hidden = true));
      this.removeAttribute('data-panel-open');
      delete this.dataset.panel;
    }

    showResult(result, focus) {
      var best = this.store.best || result;
      var el = this.screens.result;
      var left = this.cfg.attempts - this.store.attempts;
      el.querySelector('[data-result-label]').textContent = result.done ? 'You detangled in' : 'Time! You cleared';
      el.querySelector('[data-result-time]').innerHTML = result.done
        ? secs(result.ms) + '<small>s</small>'
        : result.cleared + '<small>/' + this.cfg.knots + '</small>';
      var keptBest = best !== result && this.rank(best) < this.rank(result);
      el.querySelector('[data-result-badge]').textContent = (keptBest ? 'Your best: ' : 'You won ') + best.label;

      var near = el.querySelector('[data-result-near]');
      var idx = this.rank(result);
      near.textContent = '';
      if (result.done && idx > 0 && idx < 3) {
        var next = this.cfg.tiers[idx - 1];
        var gap = (result.ms - next.ms) / 1000;
        if (gap <= 1.25) near.innerHTML = 'So close! Just <strong>' + Math.max(gap, 0.01).toFixed(2) + 's</strong> from ' + next.label + '.';
      } else if (!result.done) {
        near.textContent = 'Everyone wins. Tip: tap Spray first, then brush.';
      } else if (idx === 0) {
        near.textContent = 'Top tier. That is elite detangling.';
      }
      if (left > 0 && idx > 0) near.innerHTML += (near.innerHTML ? ' ' : '') + '<br>' + left + (left === 1 ? ' try' : ' tries') + ' left.';

      var retry = el.querySelector('[data-retry]');
      retry.hidden = left <= 0 || idx === 0;
      retry.textContent = 'Try again';
      el.querySelector('[data-claim]').textContent = 'Claim my reward';
      this.hud.hidden = true;
      this.sprayBtn.hidden = true;
      this.showPanel('result', focus);
    }

    showClaim() {
      var best = this.store.best;
      this.screens.claim.querySelector('[data-claim-reward]').textContent = best ? best.label : this.cfg.tiers[2].label;
      this.showPanel('claim');
      if (!('ontouchstart' in window)) setTimeout(() => this.input.focus({ preventScroll: true }), 50);
    }

    showDone(focus) {
      var best = this.store.best || { label: this.cfg.tiers[2].label };
      var el = this.screens.done;
      el.querySelector('[data-done-time]').textContent = best.done ? secs(best.ms) + 's' : '🎉';
      el.querySelector('[data-done-text]').textContent =
        'Your ' + best.label + ' code is on its way to ' + (this.store.email || 'your inbox') + '. Think your friends can beat ' + (best.done ? secs(best.ms) + 's' : 'that') + '?';
      this.state = 'done';
      this.dataset.state = 'done';
      this.hud.hidden = true;
      this.sprayBtn.hidden = true;
      this.showPanel('done', focus);
    }

    /* ================= claim ================= */

    async onSubmit(e) {
      e.preventDefault();
      if (this.busy) return;
      var email = this.input.value.trim().toLowerCase();
      this.input.value = email;
      if (!EMAIL_RE.test(email)) {
        this.input.setAttribute('aria-invalid', 'true');
        this.msg.textContent = 'Please enter a valid email so we can send your code.';
        return;
      }
      var at = email.lastIndexOf('@');
      var fix = DOMAIN_FIXES[email.slice(at + 1)] && email.slice(0, at + 1) + DOMAIN_FIXES[email.slice(at + 1)];
      if (fix && this.suggested !== email) {
        this.suggested = email;
        this.msg.textContent = 'Did you mean ';
        var b = document.createElement('button');
        b.type = 'button';
        b.textContent = fix;
        b.addEventListener('click', () => { this.input.value = fix; this.msg.textContent = ''; this.form.requestSubmit(); });
        this.msg.append(b, '?');
        return;
      }
      this.busy = true;
      this.submitBtn.setAttribute('aria-busy', 'true');
      try {
        await this.subscribe(email);
      } catch (err) {
        this.busy = false;
        this.submitBtn.removeAttribute('aria-busy');
        this.msg.textContent = 'Something went wrong on our end. Please try again.';
        return;
      }
      this.busy = false;
      this.submitBtn.removeAttribute('aria-busy');
      this.store.claimed = true;
      this.store.email = email;
      writeStore(this.store);
      var best = this.store.best || {};
      track('generate_lead', { lead_source: 'insert_challenge', method: 'email', insert_card: this.card, tier: best.tier });
      if (this.hasSms) this.showPanel('sms');
      else this.showDone(true);
    }

    async subscribe(email) {
      var d = this.dataset;
      var best = this.store.best || { tier: 'skipped', label: this.cfg.tiers[2].label };
      var properties = {
        'Signup Source': d.source || 'TikTok Shop Insert',
        'TikTok Shop Buyer': true,
        'Insert Card': this.card,
        'Challenge Tier': best.tier,
        'Challenge Reward': best.label,
        'Challenge Time': best.done ? Number(secs(best.ms)) : null,
        'Challenge Attempts': this.store.attempts
      };
      try {
        if (window.klaviyo && typeof window.klaviyo.identify === 'function') window.klaviyo.identify(Object.assign({ $email: email }, properties));
      } catch (e) { /* optional */ }
      if (!d.companyId) throw new Error('Missing Klaviyo company id');
      var post = (path, data) =>
        fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(d.companyId), {
          method: 'POST',
          headers: { 'content-type': 'application/vnd.api+json', revision: '2026-01-15' },
          body: JSON.stringify({ data: data }),
          keepalive: true
        }).then((res) => {
          if (!res.ok) throw new Error('Klaviyo ' + path + ' ' + res.status);
          return res;
        });

      if (d.listId) {
        await post('subscriptions', {
          type: 'subscription',
          attributes: {
            custom_source: properties['Signup Source'],
            profile: { data: { type: 'profile', attributes: { email: email, properties: properties, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } }
          },
          relationships: { list: { data: { type: 'list', id: d.listId } } }
        });
      } else {
        // eslint-disable-next-line no-console
        console.warn('[ff-detangle-challenge] No Klaviyo list id set; profile identified, not subscribed.');
        await post('profiles', { type: 'profile', attributes: { email: email, properties: properties } });
      }
      // The flow can trigger on (or split by) this event; best effort.
      try {
        await post('events', {
          type: 'event',
          attributes: {
            properties: properties,
            metric: { data: { type: 'metric', attributes: { name: 'Completed 7-Second Challenge' } } },
            profile: { data: { type: 'profile', attributes: { email: email } } }
          }
        });
      } catch (e) { /* best effort */ }
    }

    /* ================= share ================= */

    async share() {
      var best = this.store.best || {};
      var time = best.done ? secs(best.ms) : null;
      var base = this.dataset.shareUrl || window.location.origin + window.location.pathname;
      var url = base + (base.indexOf('?') > -1 ? '&' : '?') + 'utm_source=challenge_share&utm_medium=social&utm_campaign=second_order';
      var text = time
        ? 'I detangled in ' + time + 's on the UNITE 7-Second Challenge. Bet you can’t beat me 👀'
        : 'Can you detangle it in 7 seconds? 👀';
      var method = 'none';
      try {
        var blob = await this.shareImage(time);
        var file = blob && new File([blob], 'unite-7-second-challenge.png', { type: 'image/png' });
        if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
          method = 'share_image';
          await navigator.share({ files: [file], text: text + ' ' + url });
        } else if (navigator.share) {
          method = 'share_link';
          await navigator.share({ text: text, url: url });
        } else {
          method = 'download';
          if (blob) {
            var a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = 'unite-7-second-challenge.png';
            a.click();
            setTimeout(() => URL.revokeObjectURL(a.href), 2000);
          }
          try { await navigator.clipboard.writeText(text + ' ' + url); } catch (e) { /* ignore */ }
        }
      } catch (e) {
        if (e && e.name === 'AbortError') method = 'cancelled';
      }
      track('challenge_share', { insert_card: this.card, method: method, tier: best.tier });
    }

    async shareImage(time) {
      try { await document.fonts.load('600 120px Chalet'); } catch (e) { /* fallback font */ }
      var W = 1080, H = 1920;
      var c = document.createElement('canvas');
      c.width = W; c.height = H;
      var g = c.getContext('2d');
      var bg = g.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, '#1B2025');
      bg.addColorStop(1, INK);
      g.fillStyle = bg;
      g.fillRect(0, 0, W, H);
      // flowing strands across the bottom
      for (var i = 0; i < 140; i++) {
        var bx = (i / 140) * W, ph = Math.random() * 6;
        g.beginPath();
        for (var y = 1150; y <= H + 20; y += 14) {
          var x = bx + Math.sin(y * 0.006 + ph) * 26;
          y === 1150 ? g.moveTo(x, y) : g.lineTo(x, y);
        }
        g.strokeStyle = i % 9 === 0 ? 'rgba(98,181,229,0.55)' : 'rgba(243,244,245,0.10)';
        g.lineWidth = i % 9 === 0 ? 2 : 1.4;
        g.stroke();
      }
      var fade = g.createLinearGradient(0, 1150, 0, 1420);
      fade.addColorStop(0, INK);
      fade.addColorStop(1, 'rgba(18,22,25,0)');
      g.fillStyle = fade;
      g.fillRect(0, 1140, W, 290);
      var font = (px) => '600 ' + px + 'px Chalet, "Helvetica Neue", Helvetica, Arial, sans-serif';
      g.textAlign = 'center';
      g.fillStyle = '#F3F4F5';
      g.font = font(64);
      if ('letterSpacing' in g) g.letterSpacing = '18px';
      g.fillText('UNITE', W / 2, 200);
      g.fillStyle = ACCENT;
      g.font = font(34);
      if ('letterSpacing' in g) g.letterSpacing = '10px';
      g.fillText('THE 7-SECOND CHALLENGE', W / 2, 290);
      if ('letterSpacing' in g) g.letterSpacing = '0px';
      g.fillStyle = '#F3F4F5';
      g.font = font(76);
      g.fillText(time ? 'I detangled in' : 'Can you detangle it', W / 2, 640);
      g.font = font(time ? 320 : 240);
      g.fillText(time ? time + 's' : 'in 7s?', W / 2, 960);
      g.fillStyle = ACCENT;
      g.font = font(84);
      g.fillText(time ? 'Can you beat me?' : 'Your move.', W / 2, 1110);
      g.fillStyle = 'rgba(243,244,245,0.7)';
      g.font = font(40);
      g.fillText('unitehair.com', W / 2, H - 120);
      return new Promise((res) => c.toBlob(res, 'image/png'));
    }

    /* ================= render ================= */

    renderTimer(ms) {
      var late = ms > this.cfg.tiers[1].ms;
      this.timerEl.innerHTML = secs(ms) + '<small>s</small>';
      this.timerEl.classList.toggle('is-late', late && this.state === 'play');
      this.barEl.style.width = clamp(ms / this.cfg.duration, 0, 1) * 100 + '%';
    }

    frame(now) {
      this._raf = requestAnimationFrame((t) => this.frame(t));
      if (!this.W) return;
      if (this.state === 'play') {
        var el = now - this.t0;
        if (el >= this.cfg.duration) this.finish(false);
        else this.renderTimer(el);
      }
      var g = this.ctx, W = this.W, H = this.H;
      var wet = now < this.wetUntil;
      g.clearRect(0, 0, W, H);
      if (wet) {
        g.fillStyle = 'rgba(98,181,229,0.07)';
        g.fillRect(0, 0, W, H);
      }

      // strands: a soft wave, pulled and curled into each knot in proportion
      // to how tangled it still is. Drawn as smoothed quadratic curves.
      var knots = this.knots;
      var palette = wet ? HAIR_WET : HAIR;
      var sway = reducedMotion ? 0 : now * 0.0006;
      var live = [];
      for (var q = 0; q < knots.length; q++) {
        if (knots[q].hp > 0) live.push({ x: this.kx(knots[q]), y: this.ky(knots[q]), f: knots[q].hp / KNOT_HP });
      }
      var STEP = 8;
      for (var i = 0; i < this.strands.length; i++) {
        var s = this.strands[i];
        var bx = this.bandX - 40 + s.u * (this.band + 80);
        var yEnd = H * s.end;
        var px = null, py = null;
        g.beginPath();
        for (var y = -10; y <= yEnd; y += STEP) {
          var x = bx + Math.sin(y * s.freq + s.ph + sway) * s.amp;
          for (var j = 0; j < live.length; j++) {
            var k = live[j], dx = k.x - bx;
            if (dx > 120 || dx < -120) continue;
            var dy = y - k.y;
            if (dy > 190 || dy < -190) continue;
            var w = k.f * Math.exp(-(dy * dy) / 7200) * Math.exp(-(dx * dx) / 3800);
            x += dx * w * 0.92 + Math.sin(dy * 0.055 + s.ph) * 22 * w;
          }
          if (px === null) g.moveTo(x, y);
          else g.quadraticCurveTo(px, py, (px + x) / 2, (py + y) / 2);
          px = x; py = y;
        }
        g.lineTo(px, py);
        g.strokeStyle = palette[s.c];
        g.lineWidth = s.w;
        g.stroke();
      }
      // wide (desktop) stages: feather the sides of the hair into the backdrop
      if (this.band < W - 40) {
        g.save();
        g.globalCompositeOperation = 'destination-out';
        var lx0 = this.bandX - 40, lx1 = this.bandX + 110;
        var lf = g.createLinearGradient(lx0, 0, lx1, 0);
        lf.addColorStop(0, 'rgba(0,0,0,1)');
        lf.addColorStop(1, 'rgba(0,0,0,0)');
        g.fillStyle = lf;
        g.fillRect(0, 0, lx1, H);
        var rx0 = W - lx1, rx1 = W - lx0;
        var rf = g.createLinearGradient(rx0, 0, rx1, 0);
        rf.addColorStop(0, 'rgba(0,0,0,0)');
        rf.addColorStop(1, 'rgba(0,0,0,1)');
        g.fillStyle = rf;
        g.fillRect(rx0, 0, W - rx0, H);
        g.restore();
      }
      // roots fade into the backdrop so strands don't start on a hard edge
      var roots = g.createLinearGradient(0, 0, 0, 70);
      roots.addColorStop(0, 'rgba(221,227,232,0.95)');
      roots.addColorStop(1, 'rgba(221,227,232,0)');
      g.fillStyle = roots;
      g.fillRect(0, 0, W, 70);
      // wet shine
      if (wet) {
        var sh = g.createLinearGradient(0, 0, W, 0);
        sh.addColorStop(0, 'rgba(255,255,255,0)');
        sh.addColorStop(0.5, 'rgba(255,255,255,0.10)');
        sh.addColorStop(1, 'rgba(255,255,255,0)');
        g.fillStyle = sh;
        g.fillRect(0, 0, W, H);
      }

      // knots
      for (var m = 0; m < knots.length; m++) {
        var kn = knots[m];
        if (!kn.alive) continue;
        var ff = kn.hp / KNOT_HP;
        var shake = kn.shake * 3;
        kn.shake *= 0.85;
        var cx = this.kx(kn) + rand(-shake, shake), cy = this.ky(kn) + rand(-shake, shake);
        if (this.state === 'ready' && !reducedMotion) {
          g.beginPath();
          g.arc(cx, cy, 36 + Math.sin(now * 0.006 + m) * 4, 0, Math.PI * 2);
          g.strokeStyle = 'rgba(98,181,229,0.75)';
          g.lineWidth = 2;
          g.setLineDash([5, 6]);
          g.stroke();
          g.setLineDash([]);
        }
        var nLoops = Math.ceil(kn.loops.length * (0.35 + 0.65 * ff));
        g.lineWidth = 1.2;
        g.strokeStyle = wet ? 'rgba(12,14,16,0.9)' : 'rgba(22,19,16,0.85)';
        for (var l = 0; l < nLoops; l++) {
          var lp = kn.loops[l], sc = 0.4 + 0.6 * ff;
          g.beginPath();
          g.ellipse(cx + lp.ox * sc, cy + lp.oy * sc, lp.rx * sc, lp.ry * sc, lp.rot + (reducedMotion ? 0 : now * 0.0004 * lp.spin), 0, Math.PI * 2);
          g.stroke();
        }
      }

      // particles
      for (var p = this.particles.length - 1; p >= 0; p--) {
        var pa = this.particles[p];
        pa.life++;
        var life = 1 - pa.life / pa.max;
        if (life <= 0) { this.particles.splice(p, 1); continue; }
        if (pa.t === 'mist') {
          pa.x += pa.vx; pa.y += pa.vy; pa.vx *= 0.95; pa.vy *= 0.95; pa.r += 0.35;
          g.beginPath();
          g.arc(pa.x, pa.y, pa.r, 0, Math.PI * 2);
          g.fillStyle = 'rgba(190,226,246,' + 0.45 * life + ')';
          g.fill();
        } else if (pa.t === 'fiber') {
          pa.x += pa.vx; pa.y += pa.vy; pa.vy += 0.15; pa.a += 0.08;
          g.beginPath();
          g.moveTo(pa.x, pa.y);
          g.lineTo(pa.x + Math.cos(pa.a) * pa.len, pa.y + Math.sin(pa.a) * pa.len);
          g.strokeStyle = 'rgba(30,24,20,' + 0.8 * life + ')';
          g.lineWidth = 1.1;
          g.stroke();
        } else if (pa.t === 'check') {
          var yy = pa.y - (1 - life) * 40;
          g.beginPath();
          g.arc(pa.x, yy, 17, 0, Math.PI * 2);
          g.fillStyle = 'rgba(98,181,229,' + life + ')';
          g.fill();
          g.beginPath();
          g.moveTo(pa.x - 7, yy);
          g.lineTo(pa.x - 2, yy + 5);
          g.lineTo(pa.x + 8, yy - 6);
          g.strokeStyle = 'rgba(255,255,255,' + life + ')';
          g.lineWidth = 3;
          g.stroke();
        }
      }

      // the comb follows the finger / cursor
      if (this.pointer && (this.state === 'ready' || this.state === 'play')) this.drawComb(g, this.pointer.x, this.pointer.y, this.down);

      this.drawConfetti();
    }

    drawComb(g, x, y, active) {
      g.save();
      g.translate(x, y);
      g.rotate(-0.06);
      g.shadowColor = 'rgba(18,22,25,0.25)';
      g.shadowBlur = active ? 10 : 6;
      g.shadowOffsetY = 3;
      g.fillStyle = ACCENT;
      var w = 96;
      if (g.roundRect) { g.beginPath(); g.roundRect(-w / 2, -30, w, 16, 8); g.fill(); }
      else g.fillRect(-w / 2, -30, w, 16);
      g.shadowBlur = 0;
      for (var i = 0; i < 12; i++) g.fillRect(-w / 2 + 5 + i * 7.6, -16, 3.6, 24);
      g.restore();
    }

    burst(n) {
      if (reducedMotion) return;
      var colors = [ACCENT, INK, '#FFFFFF', '#9FD3F0'];
      for (var i = 0; i < n; i++) {
        this.confetti.push({
          x: this.W / 2 + rand(-60, 60), y: this.H * 0.45, vx: rand(-9, 9), vy: rand(-15, -5),
          r: rand(0, Math.PI), vr: rand(-0.3, 0.3), w: rand(6, 11), h: rand(3, 6), c: colors[i % colors.length], life: 0
        });
      }
    }

    drawConfetti() {
      var g = this.fxCtx;
      g.clearRect(0, 0, this.W, this.H);
      for (var i = this.confetti.length - 1; i >= 0; i--) {
        var c = this.confetti[i];
        c.life++;
        c.x += c.vx; c.y += c.vy; c.vy += 0.38; c.vx *= 0.99; c.r += c.vr;
        if (c.y > this.H + 20 || c.life > 240) { this.confetti.splice(i, 1); continue; }
        g.save();
        g.translate(c.x, c.y);
        g.rotate(c.r);
        g.fillStyle = c.c;
        g.fillRect(-c.w / 2, -c.h / 2, c.w, c.h);
        g.restore();
      }
    }
  }

  customElements.define('ff-detangle-challenge', FfDetangleChallenge);
})();
